import { RippleButton, type RippleButtonProps } from "@/components/ui/ripple-button";
import { cn } from "@/lib/utils";

// MUI's `IconButton`, as a shared Atlas component.
//
// Three surfaces built their own before this existed — the autocomplete's popup
// indicator, `icon-menu-button`'s trigger, and `tree-select`'s expand/collapse
// toggle — and they disagreed about things a reader would expect to be settled:
// whether the ripple is centred, whether the control clips itself, and which
// ink the glyph carries. `CLAUDE.md` is explicit that a shared look is a wrapper
// component rather than per-call-site classes, and `migration-guidelines.md`
// has a shared donor component porting globally at first encounter.
//
// The values are `IconButton.js:53-71` and the `ButtonBase` under it:
//
//   - `padding: 8`, `borderRadius: '50%'`, `color: palette.action.active`,
//   - a `background-color` transition over `duration.shortest` (150ms),
//   - hover at `alpha(action.active, action.hoverOpacity)` — `rgba(0,0,0,0.04)`,
//   - disabled at `action.disabled` over a transparent ground (`:143-146`),
//     taking no pointer events (`ButtonBase.mjs:69-73`),
//   - and **`centerRipple: true`** (`:215`), which `IconButton` is alone among
//     MUI's four `ButtonBase` users in setting.
//
// **It does not clip itself, deliberately.** `overflow: hidden` on a round
// control clips the child's *hit area* as well as its paint, so a
// `border-radius: 50%` button stops answering presses in the corners its square
// glyph still covers — measured on the caret at 641 pressable pixels against the
// donor's 670, with a corner press falling through to the control underneath.
// MUI does not have the problem because the clip lives on the ripple layer, an
// inset child, while the button stays `overflow: visible`. `useTouchRipple`'s
// layer clips itself the same way, so there is nothing to put here.
//
// **Focus is a ring, not MUI's breathing ripple.** `ButtonBase` drops the
// outline (`ButtonBase.js:56`) and pulses a centred ripple on a 2500ms loop
// instead (`:202`). This app ruled against porting that — `touch-ripple.tsx`
// records why — so an icon button takes the ring the rest of the app uses. A
// named divergence, and the app's standing answer to it rather than a fresh one.
//
// Re-reported against the category tree's expander, which is one of these, and
// **ruled kept** there too (#542 decision round, D5, 2026-09-22): the donor
// draws no ring on it, and the ring stays. Removing a focus indicator to match
// a source that lacks one is an accessibility regression wearing parity's
// clothes, and this control is shared, so the change would have landed
// app-wide. Recorded here rather than at the expander because here is where
// the ring is drawn.

/**
 * MUI's three sizes, as padding around the glyph (`IconButton.js:126-140`).
 *
 * Padding rather than a width: MUI sizes an icon button by padding a glyph
 * whose own size the caller sets, so a 24px icon at `medium` measures 40px and
 * the same icon at `small` measures 34. Spelling it as a width instead would
 * make every caller's glyph size load-bearing in a second place.
 */
const SIZE_PADDING = {
  small: "p-[5px]",
  medium: "p-2",
  large: "p-3",
} as const;

/**
 * MUI's `color` (`IconButton.mjs`): the glyph in the palette colour's `main`
 * and the hover ground that colour at `action.hoverOpacity` (0.04). `inherit`
 * takes its ink from the parent and keeps the default's grey ground.
 *
 * The error ground is its own `rgba` token, not `lennar-error/4`: a Tailwind
 * alpha modifier compiles to `color-mix(in oklab, …)`, a different colour and
 * one unreadable beside the number it came from.
 */
const COLOR_CLASSES = {
  default: { ink: "text-lennar-action-active", hover: "hover:bg-lennar-action-hover" },
  inherit: { ink: "text-inherit", hover: "hover:bg-lennar-action-hover" },
  primary: { ink: "text-lennar-blue-600", hover: "hover:bg-lennar-primary-hover" },
  error: { ink: "text-lennar-error", hover: "hover:bg-lennar-error-hover" },
} as const;

export type IconButtonColor = keyof typeof COLOR_CLASSES;

export interface IconButtonProps extends Omit<RippleButtonProps, "center" | "color"> {
  /** MUI's `size` — `padding: 5 | 8 | 12` (`IconButton.js:126-140`). */
  size?: keyof typeof SIZE_PADDING;
  /**
   * MUI's `disableRipple`, for the surfaces the donor turns press feedback off
   * on.
   *
   * **It takes the hover tint with it.** On `IconButton` alone this is not the
   * "drops the ripple and nothing else" opt-out it is elsewhere: the whole
   * hover rule lives inside a `props => !props.disableRipple` variant
   * (`IconButton.js:64-75`), so a `disableRipple` icon button in the source
   * has no background response at all, only its glyph.
   */
  disableRipple?: boolean;
  /** MUI's `color` — `default` when omitted. */
  color?: IconButtonColor;
}

/**
 * A round icon button with MUI's centred ripple.
 *
 * The glyph is the caller's child and the caller sizes it — `size` here is the
 * padding around it, as it is in MUI. A caller needing a shape MUI itself
 * overrides (the popup indicator's `padding: 2`, the outlined menu trigger's
 * `border-radius: 5px`) passes it through `className`, which `cn` merges over
 * the defaults.
 */
export function IconButton({
  size = "medium",
  disableRipple = false,
  color = "default",
  className,
  ...props
}: IconButtonProps) {
  return (
    <RippleButton
      data-slot="icon-button"
      // `centerRipple`, which `IconButton` is alone among MUI's four
      // `ButtonBase` users in setting (`IconButton.js:215`).
      center
      // `focusRipple` (`IconButton.js:217`) — Space activation on, which is
      // not the default. See `RippleOptions.focusRipple`.
      focusRipple
      disableRipple={disableRipple}
      {...props}
      className={cn(
        // `relative` and the unclipped host come from `RippleButton`; see the
        // header for why the clip stays off the button.
        "inline-flex shrink-0 items-center justify-center rounded-[50%]",
        // `cursor-pointer` because Tailwind v4's preflight dropped v3's
        // `button { cursor: pointer }`; `ButtonBase.js:63` sets it, and drops
        // to `default` only when disabled (`:79`).
        "cursor-pointer disabled:cursor-default",
        // And `pointer-events: none` when disabled (`ButtonBase.mjs:69-73`):
        // a press lands on whatever lies under the button, which shows its own
        // cursor — a struck line's ⋮ makes the row the Active selection under
        // the row's pointer (#965 N10). Every caller is an MUI donor, so the
        // rule is global, as it is in MUI.
        "disabled:pointer-events-none",
        COLOR_CLASSES[color].ink,
        "transition-[background-color] duration-150 ease-in-out",
        // The hover tint is inside the donor's `!disableRipple` variant
        // (`IconButton.js:64-75`), so it goes when the ripple does. Tailwind
        // v4's `hover:` already compiles to `@media (hover: hover)`, which is
        // the donor's `@media (hover: none) { transparent }` reset (`:71-73`).
        !disableRipple && COLOR_CLASSES[color].hover,
        "disabled:text-lennar-action-disabled disabled:bg-transparent",
        "focus-visible:outline-lennar-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2",
        SIZE_PADDING[size],
        className,
      )}
    />
  );
}
